import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMIDILearn } from './MIDILearnManager';
import { Settings, Radio, Trash2, Volume2, Keyboard, Plug2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

export default function SettingsModal({ open, onOpenChange }) {
  const {
    midiMappings,
    midiDevices,
    selectedDevice,
    setSelectedDevice,
    setupMIDIListener,
    clearMapping,
    clearAllMappings
  } = useMIDILearn();

  const [settings, setSettings] = useState(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem('harmonicmixer_settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to load settings');
      }
    }
    return {
      // Audio Settings
      masterVolume: 80,
      crossfaderCurve: 50,
      autoGainControl: false,
      latency: 10, // ms
      audioDriver: 'WebAudio', // 'WebAudio', 'ASIO', 'CoreAudio'
      sampleRate: 44100,
      bufferSize: 256,
      syncTolerance: 0.1, // seconds
      
      // Visual Settings
      waveformQuality: 'high',
      waveformColor: 'default',
      showVUMeters: true,
      showSpectrum: true,
      
      // DJ Features
      autoSync: false,
      autoCrossfade: false,
      defaultPitchRange: 8,
      
      // Interface
      enableKeyboardShortcuts: true,
      showToastNotifications: true,
      defaultLayout: 'battle',
      fullscreenOnStartup: false,
      
      // MIDI
      midiEnabled: true,
      midiAutoConnect: true,
      
      // Library
      autoCreateCrates: true,
      detectDuplicates: true,
      scanSubfolders: true,
      
      // Integrations
      spotifyEnabled: false,
      spotifyClientId: '',
      spotifyClientSecret: ''
    };
  });

  // Save settings whenever they change
  useEffect(() => {
    localStorage.setItem('harmonicmixer_settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    toast.success(`Setting updated: ${key}`);
  };

  const saveSpotifyCredentials = async () => {
    if (!settings.spotifyClientId || !settings.spotifyClientSecret) {
      toast.error('Please enter both Client ID and Client Secret');
      return;
    }

    try {
      const response = await axios.post(`${API}/integrations/spotify/credentials`, {
        client_id: settings.spotifyClientId,
        client_secret: settings.spotifyClientSecret
      });

      if (response.data.status === 'success') {
        toast.success('✅ Spotify credentials saved successfully!');
      }
    } catch (error) {
      console.error('Error saving Spotify credentials:', error);
      toast.error('Failed to save Spotify credentials');
    }
  };

  const mappingsList = Object.entries(midiMappings).map(([key, mapping]) => ({
    key,
    ...mapping
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] bg-gray-900 border-cyan-500/30 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2 text-cyan-400">
            <Settings className="w-6 h-6" />
            DJ Mixer Settings
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Configure your DJ mixer preferences and MIDI mappings
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-6 bg-gray-800">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="audio">Audio</TabsTrigger>
            <TabsTrigger value="visual">Visual</TabsTrigger>
            <TabsTrigger value="integrations">
              Integrations
              {settings.spotifyEnabled && (
                <Badge className="ml-2 bg-green-600">✓</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="midi">
              MIDI
              {midiDevices.length > 0 && (
                <Badge className="ml-2 bg-green-600">{midiDevices.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="shortcuts">Shortcuts</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-4">
            <div className="space-y-4 p-4">
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <Label className="text-white">Keyboard Shortcuts</Label>
                  <p className="text-xs text-gray-400">Enable keyboard controls</p>
                </div>
                <Switch
                  checked={settings.enableKeyboardShortcuts}
                  onCheckedChange={(v) => updateSetting('enableKeyboardShortcuts', v)}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <Label className="text-white">Toast Notifications</Label>
                  <p className="text-xs text-gray-400">Show on-screen notifications</p>
                </div>
                <Switch
                  checked={settings.showToastNotifications}
                  onCheckedChange={(v) => updateSetting('showToastNotifications', v)}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <Label className="text-white">Fullscreen on Startup</Label>
                  <p className="text-xs text-gray-400">Open mixer in fullscreen mode</p>
                </div>
                <Switch
                  checked={settings.fullscreenOnStartup}
                  onCheckedChange={(v) => updateSetting('fullscreenOnStartup', v)}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Default Layout</Label>
                <div className="grid grid-cols-2 gap-2">
                  {['battle', 'producer'].map(layout => (
                    <Button
                      key={layout}
                      onClick={() => updateSetting('defaultLayout', layout)}
                      className={`${
                        settings.defaultLayout === layout
                          ? 'bg-cyan-600 hover:bg-cyan-700'
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      {layout === 'battle' ? '⚔️ Battle DJ' : '🎛️ Producer'}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Audio Settings */}
          <TabsContent value="audio" className="space-y-4">
            <div className="space-y-4 p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-white">Master Volume</Label>
                  <span className="text-cyan-400 font-bold">{settings.masterVolume}%</span>
                </div>
                <Slider
                  value={[settings.masterVolume]}
                  onValueChange={(v) => updateSetting('masterVolume', v[0])}
                  max={100}
                  className="cursor-pointer"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-white">Crossfader Curve</Label>
                  <span className="text-cyan-400 font-bold">{settings.crossfaderCurve}%</span>
                </div>
                <Slider
                  value={[settings.crossfaderCurve]}
                  onValueChange={(v) => updateSetting('crossfaderCurve', v[0])}
                  max={100}
                  className="cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <Label className="text-white">Auto BPM Sync</Label>
                  <p className="text-xs text-gray-400">Automatically sync BPMs when loading tracks</p>
                </div>
                <Switch
                  checked={settings.autoSync}
                  onCheckedChange={(v) => updateSetting('autoSync', v)}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <Label className="text-white">Auto Gain Control</Label>
                  <p className="text-xs text-gray-400">Normalize track volumes automatically</p>
                </div>
                <Switch
                  checked={settings.autoGainControl}
                  onCheckedChange={(v) => updateSetting('autoGainControl', v)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-white">Default Pitch Range</Label>
                  <span className="text-cyan-400 font-bold">±{settings.defaultPitchRange}%</span>
                </div>
                <Slider
                  value={[settings.defaultPitchRange]}
                  onValueChange={(v) => updateSetting('defaultPitchRange', v[0])}
                  min={6}
                  max={50}
                  step={2}
                  className="cursor-pointer"
                />
                <p className="text-xs text-gray-400">Default pitch control range for new tracks</p>
              </div>

              <div className="border-t border-gray-700 pt-4 mt-4">
                <h3 className="text-white font-semibold mb-4">🎚️ Advanced Audio Settings</h3>
                
                <div className="space-y-4">
                  {/* Audio Driver */}
                  <div className="space-y-2">
                    <Label className="text-white">Audio Driver</Label>
                    <select
                      value={settings.audioDriver}
                      onChange={(e) => updateSetting('audioDriver', e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg p-2"
                    >
                      <option value="WebAudio">Web Audio API (Default)</option>
                      <option value="CoreAudio">Core Audio (Mac)</option>
                      <option value="ASIO">ASIO (Windows/Professional)</option>
                    </select>
                    <p className="text-xs text-gray-400">
                      {settings.audioDriver === 'WebAudio' && 'Standard browser audio - works everywhere'}
                      {settings.audioDriver === 'CoreAudio' && 'Optimized for Mac - lower latency'}
                      {settings.audioDriver === 'ASIO' && 'Professional audio - lowest latency (requires ASIO drivers)'}
                    </p>
                  </div>

                  {/* Latency */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-white">Audio Latency</Label>
                      <span className="text-cyan-400 font-bold">{settings.latency}ms</span>
                    </div>
                    <Slider
                      value={[settings.latency]}
                      onValueChange={(v) => updateSetting('latency', v[0])}
                      min={1}
                      max={50}
                      step={1}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-400">Lower = more responsive, Higher = more stable (5-10ms ideal)</p>
                  </div>

                  {/* Sample Rate */}
                  <div className="space-y-2">
                    <Label className="text-white">Sample Rate</Label>
                    <select
                      value={settings.sampleRate}
                      onChange={(e) => updateSetting('sampleRate', parseInt(e.target.value))}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg p-2"
                    >
                      <option value="44100">44.1 kHz (CD Quality)</option>
                      <option value="48000">48 kHz (Professional)</option>
                      <option value="96000">96 kHz (High-End)</option>
                      <option value="192000">192 kHz (Studio)</option>
                    </select>
                    <p className="text-xs text-gray-400">Higher quality = more CPU usage</p>
                  </div>

                  {/* Buffer Size */}
                  <div className="space-y-2">
                    <Label className="text-white">Buffer Size</Label>
                    <select
                      value={settings.bufferSize}
                      onChange={(e) => updateSetting('bufferSize', parseInt(e.target.value))}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg p-2"
                    >
                      <option value="64">64 samples (Ultra Low - 1.5ms)</option>
                      <option value="128">128 samples (Very Low - 3ms)</option>
                      <option value="256">256 samples (Low - 6ms)</option>
                      <option value="512">512 samples (Medium - 12ms)</option>
                      <option value="1024">1024 samples (High - 23ms)</option>
                      <option value="2048">2048 samples (Very High - 46ms)</option>
                    </select>
                    <p className="text-xs text-gray-400">Lower = less delay but requires more CPU</p>
                  </div>

                  {/* Sync Tolerance */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-white">Sync Tolerance</Label>
                      <span className="text-cyan-400 font-bold">{(settings.syncTolerance * 1000).toFixed(0)}ms</span>
                    </div>
                    <Slider
                      value={[settings.syncTolerance * 10]}
                      onValueChange={(v) => updateSetting('syncTolerance', v[0] / 10)}
                      min={0.5}
                      max={3}
                      step={0.1}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-gray-400">Max timing difference before sync correction (0.5-3.0 = 50-300ms)</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Visual Settings */}
          <TabsContent value="visual" className="space-y-4">
            <div className="space-y-4 p-4">
              <div className="space-y-2">
                <Label className="text-white">Waveform Quality</Label>
                <div className="grid grid-cols-3 gap-2">
                  {['low', 'medium', 'high'].map(quality => (
                    <Button
                      key={quality}
                      onClick={() => updateSetting('waveformQuality', quality)}
                      className={`${
                        settings.waveformQuality === quality
                          ? 'bg-cyan-600 hover:bg-cyan-700'
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      {quality.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <Label className="text-white">Show VU Meters</Label>
                  <p className="text-xs text-gray-400">Display volume level meters</p>
                </div>
                <Switch
                  checked={settings.showVUMeters}
                  onCheckedChange={(v) => updateSetting('showVUMeters', v)}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <Label className="text-white">Show Spectrum Analyzer</Label>
                  <p className="text-xs text-gray-400">Display real-time spectrum</p>
                </div>
                <Switch
                  checked={settings.showSpectrum}
                  onCheckedChange={(v) => updateSetting('showSpectrum', v)}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Waveform Color Theme</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'default', label: 'Default (Cyan/Pink)', colors: ['#22d3ee', '#f472b6'] },
                    { value: 'classic', label: 'Classic (Blue/Orange)', colors: ['#3b82f6', '#f97316'] },
                    { value: 'neon', label: 'Neon (Green/Purple)', colors: ['#10b981', '#a855f7'] },
                    { value: 'fire', label: 'Fire (Red/Yellow)', colors: ['#ef4444', '#eab308'] }
                  ].map(theme => (
                    <Button
                      key={theme.value}
                      onClick={() => updateSetting('waveformColor', theme.value)}
                      className={`${
                        settings.waveformColor === theme.value
                          ? 'bg-cyan-600 hover:bg-cyan-700'
                          : 'bg-gray-700 hover:bg-gray-600'
                      } flex items-center gap-2`}
                    >
                      <div className="flex gap-1">
                        <div className="w-3 h-3 rounded-full" style={{backgroundColor: theme.colors[0]}}></div>
                        <div className="w-3 h-3 rounded-full" style={{backgroundColor: theme.colors[1]}}></div>
                      </div>
                      <span className="text-xs">{theme.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Integrations Settings */}
          <TabsContent value="integrations" className="space-y-4">
            <div className="space-y-4 p-4">
              <div className="flex items-center gap-2 mb-4">
                <Plug2 className="w-5 h-5 text-cyan-400" />
                <h3 className="font-bold text-cyan-400">External Integrations</h3>
              </div>

              {/* Spotify Integration */}
              <div className="space-y-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white font-semibold flex items-center gap-2">
                      🎵 Spotify Integration
                    </Label>
                    <p className="text-xs text-gray-400 mt-1">
                      Search tracks and get BPM/key data from Spotify
                    </p>
                  </div>
                  <Switch
                    checked={settings.spotifyEnabled}
                    onCheckedChange={(v) => updateSetting('spotifyEnabled', v)}
                  />
                </div>

                {settings.spotifyEnabled && (
                  <div className="space-y-3 pt-3 border-t border-gray-700">
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Spotify Client ID</Label>
                      <Input
                        type="text"
                        placeholder="Enter your Spotify Client ID"
                        value={settings.spotifyClientId}
                        onChange={(e) => updateSetting('spotifyClientId', e.target.value)}
                        className="bg-gray-900 border-gray-600 text-white"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white text-sm">Spotify Client Secret</Label>
                      <Input
                        type="password"
                        placeholder="Enter your Spotify Client Secret"
                        value={settings.spotifyClientSecret}
                        onChange={(e) => updateSetting('spotifyClientSecret', e.target.value)}
                        className="bg-gray-900 border-gray-600 text-white"
                      />
                    </div>

                    <Button
                      onClick={saveSpotifyCredentials}
                      className="w-full bg-green-600 hover:bg-green-700"
                      disabled={!settings.spotifyClientId || !settings.spotifyClientSecret}
                    >
                      💾 Save Spotify Credentials
                    </Button>

                    <div className="bg-cyan-900/20 border border-cyan-500/30 rounded p-3">
                      <p className="text-xs text-cyan-300">
                        <strong>📌 How to get API keys:</strong>
                      </p>
                      <ol className="text-xs text-gray-300 mt-2 space-y-1 ml-4 list-decimal">
                        <li>Go to <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">Spotify Developer Dashboard</a></li>
                        <li>Create a new app</li>
                        <li>Copy your Client ID and Client Secret</li>
                        <li>Make sure to request <strong>audio-features</strong> scope for BPM/key data</li>
                      </ol>
                    </div>

                    {settings.spotifyClientId && settings.spotifyClientSecret && (
                      <div className="bg-green-900/20 border border-green-500/30 rounded p-3">
                        <p className="text-xs text-green-300 flex items-center gap-2">
                          ✅ <strong>API credentials entered!</strong> Click "Save Spotify Credentials" to activate.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Placeholder for future integrations */}
              <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 border-dashed">
                <p className="text-sm text-gray-400 text-center">
                  More integrations coming soon...
                </p>
              </div>
            </div>
          </TabsContent>

          {/* MIDI Settings */}
          <TabsContent value="midi" className="space-y-4">
            <div className="space-y-4 p-4">
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Radio className="w-4 h-4" />
                  Connected MIDI Devices
                </Label>
                {midiDevices.length === 0 ? (
                  <div className="p-4 bg-gray-800 rounded-lg text-center text-gray-400">
                    No MIDI devices detected. Connect a MIDI controller to get started.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {midiDevices.map((device) => (
                      <div
                        key={device.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedDevice?.id === device.id
                            ? 'bg-cyan-600/20 border-cyan-500'
                            : 'bg-gray-800 border-gray-700 hover:border-cyan-500/50'
                        }`}
                        onClick={() => {
                          setSelectedDevice(device);
                          setupMIDIListener(device);
                          toast.success(`Selected: ${device.name}`);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-white">{device.name}</p>
                            <p className="text-xs text-gray-400">{device.manufacturer || 'Unknown'}</p>
                          </div>
                          {selectedDevice?.id === device.id && (
                            <Badge className="bg-green-600">Active</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-white">MIDI Mappings ({mappingsList.length})</Label>
                  {mappingsList.length > 0 && (
                    <Button
                      size="sm"
                      onClick={clearAllMappings}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>

                {mappingsList.length === 0 ? (
                  <div className="p-4 bg-gray-800 rounded-lg text-center text-gray-400">
                    No MIDI mappings configured. Use MIDI Learn mode to create mappings.
                  </div>
                ) : (
                  <ScrollArea className="h-[300px] rounded-lg border border-gray-700">
                    <div className="p-3 space-y-2">
                      {mappingsList.map((mapping) => (
                        <div
                          key={mapping.key}
                          className="p-3 bg-gray-800 rounded-lg flex items-center justify-between"
                        >
                          <div>
                            <p className="font-semibold text-white">{mapping.label}</p>
                            <p className="text-xs text-gray-400">
                              CC{mapping.control} → {mapping.deck ? `${mapping.deck.toUpperCase()} Deck` : 'Master'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => clearMapping(mapping.key)}
                            className="bg-gray-700 hover:bg-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Keyboard Shortcuts */}
          <TabsContent value="shortcuts" className="space-y-4">
            <ScrollArea className="h-[400px] p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-4">
                  <Keyboard className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-bold text-cyan-400">Keyboard Shortcuts</h3>
                </div>

                {[
                  { key: 'SPACE', action: 'Play/Pause focused deck' },
                  { key: 'TAB', action: 'Switch focused deck' },
                  { key: '1-4', action: 'Trigger hot cue (Shift+1-4 to set)' },
                  { key: 'Q', action: 'Toggle High EQ' },
                  { key: 'W', action: 'Toggle Mid EQ' },
                  { key: 'E', action: 'Toggle Low EQ' },
                  { key: '↑', action: 'Pitch bend up (temporary)' },
                  { key: '↓', action: 'Pitch bend down (temporary)' },
                  { key: '[', action: 'Set loop start point' },
                  { key: ']', action: 'Set loop end point' },
                  { key: 'L', action: 'Toggle loop on/off' },
                  { key: 'ESC', action: 'Exit fullscreen mode' },
                  { key: 'Ctrl+S', action: 'Sync BPM between decks' }
                ].map((shortcut, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <span className="text-gray-300">{shortcut.action}</span>
                    <Badge className="bg-cyan-600 font-mono">{shortcut.key}</Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
          <Button onClick={() => onOpenChange(false)} className="bg-gray-700 hover:bg-gray-600">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
